import axios, { isAxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import type { SerialValidationRecord, SerialValidationStatus } from '@/domain/izzi';
import type { ProcessInventoryResponse } from '@/domain/inventory';
import { useHttp } from '@/hooks/http';
import { VITE_URL_SERVICE } from '@/utils/envs';

const httpPublic = axios.create({
  baseURL: VITE_URL_SERVICE,
  headers: { 'ngrok-skip-browser-warning': 'true' },
});

const RELEASED_HOURS = 168;
const DEFAULT_PAGE_SIZE = 20;

const cleanMessage = (msg: string) => msg.replace(/^(\[.*?\]\s*)+/, '').trim();

interface PageState {
  rows: SerialValidationRecord[];
  total: number;
}

const emptyPage = (): PageState => ({ rows: [], total: 0 });

interface ListFilters {
  purchaseOrder?: string;
  iccid?: string;
}

const buildListParams = (filters: ListFilters) => ({
  ...(filters.purchaseOrder ? { purchaseOrder: filters.purchaseOrder } : {}),
  ...(filters.iccid ? { iccid: filters.iccid } : {}),
});

// 0=Por PO | 1=Cuarentena | 2=Liberadas | 3=Errores
export type SeriesTab = 0 | 1 | 2 | 3;

export const canRevalidateStatus = (status: SerialValidationStatus): boolean =>
  status === 'QUARANTINED' || status === 'REJECTED' || status === 'ERROR';

export const useSeriesSection = () => {
  const http = useHttp();
  const [activeTab, setActiveTab] = useState<SeriesTab>(0);
  const [isLoading, setIsLoading] = useState(false);

  const [poFilterInput, setPoFilterInput] = useState('');
  const [iccidFilterInput, setIccidFilterInput] = useState('');
  const [appliedPoFilter, setAppliedPoFilter] = useState('');
  const [appliedIccidFilter, setAppliedIccidFilter] = useState('');

  const [poPage, setPoPage] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [quarPage, setQuarPage] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [relPage, setRelPage] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [errPage, setErrPage] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE });

  const [poData, setPoData] = useState<PageState>(emptyPage());
  const [quarData, setQuarData] = useState<PageState>(emptyPage());
  const [relData, setRelData] = useState<PageState>(emptyPage());
  const [errData, setErrData] = useState<PageState>(emptyPage());

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedError, setSelectedError] = useState<string | null>(null);

  const currentFilters = (): ListFilters => ({
    purchaseOrder: appliedPoFilter || undefined,
    iccid: appliedIccidFilter || undefined,
  });

  const fetchByPo = async (page: number, pageSize: number, filters: ListFilters) => {
    if (!filters.purchaseOrder) return;

    setIsLoading(true);
    try {
      const { data } = await httpPublic.get<{ data: SerialValidationRecord[]; total: number }>(
        '/api/v3/ops-izzi/serial-validations',
        { params: { page, pageSize, ...buildListParams(filters) } },
      );
      setPoData({ rows: data.data, total: data.total });
    } catch {
      toast.error('Error al cargar las series de la PO.');
    } finally {
      setIsLoading(false);
    }
  };

  const onPoPageChange = (model: { page: number; pageSize: number }) => {
    setPoPage(model);
    fetchByPo(model.page, model.pageSize, currentFilters());
  };

  const onDelete = async (id: string) => {
    setIsLoading(true);
    try {
      await httpPublic.delete(`/api/v3/ops-izzi/serial-validations/${id}`);
      setPoData((prev) => ({
        rows: prev.rows.filter((r) => r.id !== id),
        total: Math.max(0, prev.total - 1),
      }));
      toast.success('Serie desvinculada correctamente.');
    } catch {
      toast.error('Error al desvincular la serie.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchQuarantined = useCallback(
    async (page: number, pageSize: number, filters: ListFilters) => {
      setIsLoading(true);
      try {
        const result = await http.izzi.getSerials({
          status: 'QUARANTINED',
          page: page + 1,
          pageSize,
          ...buildListParams(filters),
        });
        setQuarData({ rows: result.data, total: result.total });
      } catch {
        setQuarData(emptyPage());
        toast.error('Error al cargar series en cuarentena.');
      } finally {
        setIsLoading(false);
      }
    },
    [http.izzi],
  );

  const onQuarPageChange = (model: { page: number; pageSize: number }) => {
    setQuarPage(model);
    fetchQuarantined(model.page, model.pageSize, currentFilters());
  };

  const fetchReleased = useCallback(
    async (page: number, pageSize: number, filters: ListFilters) => {
      setIsLoading(true);
      try {
        const result = await http.izzi.getSerials({
          status: 'RELEASED',
          hours: RELEASED_HOURS,
          page: page + 1,
          pageSize,
          ...buildListParams(filters),
        });
        setRelData({ rows: result.data, total: result.total });
      } catch {
        setRelData(emptyPage());
        toast.error('Error al cargar series liberadas.');
      } finally {
        setIsLoading(false);
      }
    },
    [http.izzi],
  );

  const onRelPageChange = (model: { page: number; pageSize: number }) => {
    setRelPage(model);
    fetchReleased(model.page, model.pageSize, currentFilters());
  };

  const fetchErrors = useCallback(async (page: number, pageSize: number, iccid?: string) => {
    setIsLoading(true);
    try {
      const { data } = await httpPublic.get<{ data: SerialValidationRecord[]; total: number }>(
        '/api/v3/ops-izzi/serial-validations/errors',
        {
          params: {
            page,
            pageSize,
            ...(iccid ? { iccid } : {}),
          },
        },
      );
      setErrData({ rows: data.data, total: data.total });
    } catch {
      toast.error('Error al cargar la lista de errores.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const onErrPageChange = (model: { page: number; pageSize: number }) => {
    setErrPage(model);
    fetchErrors(model.page, model.pageSize, appliedIccidFilter || undefined);
  };

  const refreshActiveTab = () => {
    const filters = currentFilters();

    if (activeTab === 0 && filters.purchaseOrder) {
      fetchByPo(poPage.page, poPage.pageSize, filters);
    }
    if (activeTab === 1) {
      fetchQuarantined(quarPage.page, quarPage.pageSize, filters);
    }
    if (activeTab === 2) {
      fetchReleased(relPage.page, relPage.pageSize, filters);
    }
    if (activeTab === 3) {
      fetchErrors(errPage.page, errPage.pageSize, appliedIccidFilter || undefined);
    }
  };

  const onRevalidate = async (id: string) => {
    setIsLoading(true);
    try {
      const { data } = await httpPublic.post<ProcessInventoryResponse>(
        `/api/v3/ops-izzi/serial-validations/${id}/revalidate`,
      );

      if (data.status === 'REJECTED' || data.status === 'ERROR') {
        throw new Error(cleanMessage(data.message || 'El serial sigue con error.'));
      }

      if (data.status === 'QUARANTINED') {
        toast.warning(cleanMessage(data.message || '') || 'Serie en cuarentena');
      } else {
        toast.success('Serie revalidada correctamente.');
      }

      refreshActiveTab();
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        const data = error.response.data as ProcessInventoryResponse;
        if (data?.status === 'QUARANTINED') {
          toast.warning(cleanMessage(data.message || '') || 'Serie en cuarentena');
          refreshActiveTab();
          return;
        }
      }

      const axiosMsg = isAxiosError(error) ? error.response?.data?.message : null;
      const finalMsg =
        axiosMsg || (error instanceof Error ? error.message : 'Error al revalidar.');
      toast.error(cleanMessage(String(finalMsg)));
    } finally {
      setIsLoading(false);
    }
  };

  const onOpenModal = (msg: string) => {
    setSelectedError(msg);
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
    setSelectedError(null);
  };

  const onTabChange = (_: React.SyntheticEvent, value: SeriesTab) => {
    setActiveTab(value);
    setPoFilterInput('');
    setIccidFilterInput('');
    setAppliedPoFilter('');
    setAppliedIccidFilter('');

    if (value === 1) fetchQuarantined(0, DEFAULT_PAGE_SIZE, {});
    if (value === 2) fetchReleased(0, DEFAULT_PAGE_SIZE, {});
    if (value === 3) fetchErrors(0, DEFAULT_PAGE_SIZE);
  };

  const onPoFilterChange = (value: string) => setPoFilterInput(value);
  const onIccidFilterChange = (value: string) => setIccidFilterInput(value);

  const onSearch = () => {
    const purchaseOrder = poFilterInput.trim();
    const iccid = iccidFilterInput.trim();

    if (activeTab === 0) {
      if (!purchaseOrder) {
        toast.warning('Ingresa una PO para buscar.');
        return;
      }
      setAppliedPoFilter(purchaseOrder);
      setAppliedIccidFilter(iccid);
      setPoPage((p) => ({ ...p, page: 0 }));
      fetchByPo(0, poPage.pageSize, { purchaseOrder, iccid });
      return;
    }

    if (activeTab === 3) {
      setAppliedIccidFilter(iccid);
      setErrPage((p) => ({ ...p, page: 0 }));
      fetchErrors(0, errPage.pageSize, iccid || undefined);
      return;
    }

    setAppliedPoFilter(purchaseOrder);
    setAppliedIccidFilter(iccid);
    const filters = { purchaseOrder, iccid };

    if (activeTab === 1) {
      setQuarPage((p) => ({ ...p, page: 0 }));
      fetchQuarantined(0, quarPage.pageSize, filters);
    } else if (activeTab === 2) {
      setRelPage((p) => ({ ...p, page: 0 }));
      fetchReleased(0, relPage.pageSize, filters);
    }
  };

  const onClearFilter = () => {
    setPoFilterInput('');
    setIccidFilterInput('');
    setAppliedPoFilter('');
    setAppliedIccidFilter('');

    if (activeTab === 0) {
      setPoData(emptyPage());
      return;
    }
    if (activeTab === 1) fetchQuarantined(0, quarPage.pageSize, {});
    if (activeTab === 2) fetchReleased(0, relPage.pageSize, {});
    if (activeTab === 3) fetchErrors(0, errPage.pageSize);
  };

  const onRefresh = () => {
    refreshActiveTab();
  };

  const hasAppliedFilters = !!(appliedPoFilter || appliedIccidFilter);

  return {
    activeTab,
    onTabChange,
    isLoading,
    poFilterInput,
    iccidFilterInput,
    appliedPoFilter,
    appliedIccidFilter,
    hasAppliedFilters,
    onPoFilterChange,
    onIccidFilterChange,
    onSearch,
    onClearFilter,
    onRefresh,
    poData,
    poPage,
    onPoPageChange,
    onDelete,
    quarData,
    quarPage,
    onQuarPageChange,
    relData,
    relPage,
    onRelPageChange,
    errData,
    errPage,
    onErrPageChange,
    onRevalidate,
    isModalOpen,
    selectedError,
    onOpenModal,
    onCloseModal,
  };
};
